/**
 * Offline Banner Component
 *
 * Displays a banner when the device is offline to inform users
 * that network-dependent features may not work.
 *
 * Automatically shows/hides based on network connectivity status.
 *
 * @see DRAMA-WEB-1K for network error handling improvements
 */
import { View, StyleSheet } from 'react-native';
import { TierBody } from '@dramadetector/ui';
import { useNetworkStatus } from '@dramadetector/api-client';
import Animated, { FadeInDown, FadeOutUp } from 'react-native-reanimated';

interface OfflineBannerProps {
  /**
   * Optional test ID for E2E tests
   */
  testID?: string;
}

/**
 * Banner that shows when device is offline
 *
 * @example
 * ```tsx
 * import { OfflineBanner } from '../components/OfflineBanner';
 *
 * export default function HomeScreen() {
 *   return (
 *     <View>
 *       <OfflineBanner />
 *       {/ * rest of your content * /}
 *     </View>
 *   );
 * }
 * ```
 */
export function OfflineBanner({ testID = 'offline-banner' }: OfflineBannerProps) {
  const { isOffline } = useNetworkStatus();

  if (!isOffline) {
    return null;
  }

  return (
    <Animated.View
      entering={FadeInDown.duration(300)}
      exiting={FadeOutUp.duration(300)}
      style={styles.container}
      testID={testID}
    >
      <View style={styles.content}>
        <TierBody size="sm" color="text-yellow-900" style={styles.text}>
          📵 You're offline
        </TierBody>
        <TierBody size="xs" color="text-yellow-800" style={styles.subtext}>
          Some features may not work until you reconnect
        </TierBody>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'rgba(254, 243, 199, 0.95)', // yellow-100 with opacity
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(251, 191, 36, 0.3)', // yellow-400 with opacity
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  content: {
    gap: 4,
  },
  text: {
    fontWeight: '600',
  },
  subtext: {
    opacity: 0.9,
  },
});
